import React from 'react';
import { Card, Tag, Progress, Typography, Space, Button } from 'antd';
import { 
  CalendarOutlined, 
  UserOutlined, 
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined
} from '@ant-design/icons';

const { Text, Title } = Typography;

export interface FocusTask {
  id: string;
  title: string;
  description: string;
  type: 'process' | 'artifact' | 'improvement' | 'review';
  priority: 'low' | 'medium' | 'high' | 'critical';
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  dueDate: string;
  assigneeId: string;
  assigneeName: string;
  artifactId?: string;
  artifactName?: string;
  progress?: number;
  tags?: string[];
}

interface TaskCardProps {
  task: FocusTask;
  onSelect?: (taskId: string) => void;
  onStatusChange?: (taskId: string, status: FocusTask['status']) => void;
  compact?: boolean;
}

export const TaskCard: React.FC<TaskCardProps> = ({ 
  task, 
  onSelect, 
  onStatusChange,
  compact = false 
}) => {
  const getPriorityColor = (priority: FocusTask['priority']) => {
    switch (priority) {
      case 'critical': return 'red';
      case 'high': return 'orange';
      case 'medium': return 'blue';
      case 'low': return 'green';
      default: return 'default';
    }
  };

  const getStatusColor = (status: FocusTask['status']) => {
    switch (status) {
      case 'completed': return 'green';
      case 'in_progress': return 'blue';
      case 'pending': return 'orange';
      case 'cancelled': return 'red';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: FocusTask['status']) => {
    switch (status) {
      case 'completed': return <CheckCircleOutlined />;
      case 'in_progress': return <PlayCircleOutlined />;
      case 'pending': return <ClockCircleOutlined />;
      case 'cancelled': return <PauseCircleOutlined />;
      default: return <ExclamationCircleOutlined />;
    }
  };

  const formatDueDate = (dueDate: string) => {
    const date = new Date(dueDate);
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return `Overdue by ${Math.abs(diffDays)} days`;
    if (diffDays === 0) return 'Due today';
    if (diffDays === 1) return 'Due tomorrow';
    return `Due in ${diffDays} days`;
  };

  const isOverdue = new Date(task.dueDate) < new Date() && task.status !== 'completed';

  if (compact) {
    return (
      <div
        onClick={() => onSelect?.(task.id)}
        style={{
          padding: '12px',
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '8px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
          <Text style={{ color: 'white', fontWeight: 500, fontSize: '14px' }}>
            {task.title}
          </Text>
          <Tag color={getPriorityColor(task.priority)}>
            {task.priority}
          </Tag>
        </div>
        
        <Text style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', display: 'block', marginBottom: '8px' }}>
          {task.description}
        </Text>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Tag color={getStatusColor(task.status)} icon={getStatusIcon(task.status)}>
            {task.status.replace('_', ' ')}
          </Tag>
          <Text style={{ 
            color: isOverdue ? '#ff4d4f' : 'rgba(255, 255, 255, 0.6)', 
            fontSize: '11px' 
          }}>
            {formatDueDate(task.dueDate)}
          </Text>
        </div>
      </div>
    );
  }

  return (
    <Card
      size="small"
      style={{
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '8px',
        marginBottom: '12px',
      }}
      styles={{ body: { padding: '16px' } }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
        <Title level={5} style={{ color: 'white', margin: 0, flex: 1 }}>
          {task.title}
        </Title>
        <Space>
          <Tag color={getPriorityColor(task.priority)}>
            {task.priority}
          </Tag>
          <Tag color={getStatusColor(task.status)} icon={getStatusIcon(task.status)}>
            {task.status.replace('_', ' ')}
          </Tag>
        </Space>
      </div>

      <Text style={{ color: 'rgba(255, 255, 255, 0.8)', display: 'block', marginBottom: '12px' }}>
        {task.description}
      </Text>

      {task.artifactId && task.artifactName && (
        <div style={{ marginBottom: '12px' }}>
          <Text style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '12px' }}>
            Related Artifact: 
          </Text>
          <Text style={{ color: '#1890ff', fontSize: '12px', marginLeft: '4px' }}>
            {task.artifactName}
          </Text>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <Space>
          <UserOutlined style={{ color: 'rgba(255, 255, 255, 0.6)' }} />
          <Text style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '12px' }}>
            {task.assigneeName}
          </Text>
        </Space>
        <Space>
          <CalendarOutlined style={{ color: 'rgba(255, 255, 255, 0.6)' }} />
          <Text style={{ 
            color: isOverdue ? '#ff4d4f' : 'rgba(255, 255, 255, 0.8)', 
            fontSize: '12px' 
          }}>
            {formatDueDate(task.dueDate)}
          </Text>
        </Space>
      </div>

      {task.progress !== undefined && (
        <div style={{ marginBottom: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <Text style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '12px' }}>Progress</Text>
            <Text style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '12px' }}>{task.progress}%</Text>
          </div>
          <Progress 
            percent={task.progress} 
            size="small" 
            strokeColor={task.progress === 100 ? '#52c41a' : '#1890ff'}
            trailColor="rgba(255, 255, 255, 0.1)"
          />
        </div>
      )}

      {task.tags && task.tags.length > 0 && (
        <div style={{ marginBottom: '12px' }}>
          <Space wrap>
            {task.tags.map((tag, index) => (
              <Tag key={index} color="blue">
                {tag}
              </Tag>
            ))}
          </Space>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
        {onSelect && (
          <Button 
            type="primary" 
            size="small"
            onClick={() => onSelect(task.id)}
          >
            View Details
          </Button>
        )}
        {onStatusChange && task.status !== 'completed' && (
          <Button 
            size="small"
            onClick={() => onStatusChange(task.id, 'completed')}
          >
            Mark Complete
          </Button>
        )}
      </div>
    </Card>
  );
};
